import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Shield as ShieldIcon, Gavel as GavelIcon, Fingerprint as FingerprintIcon, Lock as LockIcon,
  BrainCircuit, Database, Wallet, Info, ArrowLeft as ArrowLeftIcon,
  FileText as FileTextIcon, Download as DownloadIcon, ArrowRight as ArrowRightIcon,
  Verified as VerifiedIcon, ShieldCheck as ShieldCheckIcon, UnfoldHorizontal as UnfoldHorizontalIcon, Check
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { buildAuthModalPath } from '../lib/utils';

export const TrustScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // X-Ray Slider State
  const [sliderPos, setSliderPos] = useState(50);
  const sliderContainerRef = useRef<HTMLDivElement>(null);

  // Generator State
  const [infringingUrl, setInfringingUrl] = useState('');
  const [marketplace, setMarketplace] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!sliderContainerRef.current) return;
    const rect = sliderContainerRef.current.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const position = ((x - rect.left) / rect.width) * 100;
    setSliderPos(Math.min(Math.max(position, 0), 100));
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => {
        setGenerating(false);
        setGenerated(true);
        setTimeout(() => setGenerated(false), 3000); // Reset state after 3s
    }, 1500);
  };

  const isFormValid = infringingUrl.length > 5 && marketplace !== '';

  const openAuthModal = (mode: 'login' | 'signup') => {
    navigate(buildAuthModalPath(location.pathname, location.search, mode));
  };

  return (
    <div className="w-full select-none">
      <div className="contained-wrapper">
        <header className="h-24 flex items-center justify-between px-6 md:px-12 border-b border-[#E2E8F0] bg-white sticky top-0 z-[100]">
          <div className="flex items-center gap-3">
             <div onClick={() => navigate('/')} className="cursor-pointer">
                <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20 text-white">
                        <ShieldCheckIcon size={24} />
                    </div>
                    <span className="text-2xl font-bold tracking-tight text-slate-900">
                        {t('appName')} <span className="text-primary/50 font-normal ml-1">Trust</span>
                    </span>
                </div>
             </div>
          </div>
          <nav className="hidden md:flex items-center gap-10">
            <button
              onClick={() => scrollToSection('technology')}
              className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-primary transition-colors cursor-pointer"
            >
              {t('trust.nav.technology' as any)}
            </button>
            <button
              onClick={() => scrollToSection('legal-framework')}
              className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-primary transition-colors cursor-pointer"
            >
              {t('trust.nav.legal' as any)}
            </button>
            <button
              onClick={() => scrollToSection('resources')}
              className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-primary transition-colors cursor-pointer"
            >
              {t('trust.nav.whitepaper' as any)}
            </button>
          </nav>
          <div className="flex items-center gap-4 mr-16 md:mr-0">
            <button onClick={() => openAuthModal('login')} className="px-6 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 hover:text-primary transition-all">
                {t('nav.login')}
            </button>
            <button
              onClick={() => openAuthModal('signup')}
              className="bg-primary hover:bg-primary/90 text-white px-7 py-3 rounded-full text-xs font-extrabold uppercase tracking-widest transition-all shadow-lg shadow-primary/10 hidden md:block"
            >
                {t('trust.ctaBtn')}
            </button>
          </div>
        </header>

        <main>
          {/* Hero Section */}
          <section className="content-section flex flex-col items-center text-center bg-[#F8FAFC]">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white text-primary border border-slate-200 text-[10px] font-bold uppercase tracking-[0.2em] mb-10 shadow-sm animate-in fade-in slide-in-from-top-4 duration-700">
              <GavelIcon size={14} />
              {t('trust.badge')}
            </div>
            <h1 className="text-5xl md:text-6xl font-black leading-[1.15] tracking-tight mb-8 max-w-4xl text-slate-900 animate-in fade-in slide-in-from-bottom-4 duration-1000">
              {t('trust.title')} <br /><span className="text-primary/90">{t('trust.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-slate-500 max-w-2xl mb-16 leading-loose animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-150">
              {t('trust.subtitle')}
            </p>
            <div className="flex items-center gap-8 md:gap-16 opacity-40 grayscale pointer-events-none animate-in fade-in duration-1000 delay-300">
              <div className="flex items-center gap-2"><ShieldCheckIcon size={20} /><span className="text-xs font-bold uppercase tracking-widest">{t('trust.dmcaBadge')}</span></div>
              <div className="flex items-center gap-2"><FingerprintIcon size={20} /><span className="text-xs font-bold uppercase tracking-widest">{t('trust.forensicBadge')}</span></div>
              <div className="flex items-center gap-2"><LockIcon size={20} /><span className="text-xs font-bold uppercase tracking-widest">{t('trust.encryptedBadge')}</span></div>
            </div>
          </section>

          {/* Technology Cards */}
          <section id="technology" className="content-section border-y border-[#E2E8F0]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
              <div className="flex flex-col group">
                <div className="size-14 bg-blue-50 text-primary rounded-2xl flex items-center justify-center mb-8 border border-blue-100 group-hover:scale-110 transition-transform duration-300">
                  <BrainCircuit size={32} />
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-4 tracking-tight">{t('trust.tech1Title')}</h3>
                <p className="text-sm text-slate-500 leading-relaxed font-medium">{t('trust.tech1Desc')}</p>
              </div>
              <div className="flex flex-col group">
                <div className="size-14 bg-slate-50 text-slate-900 rounded-2xl flex items-center justify-center mb-8 border border-slate-200 group-hover:scale-110 transition-transform duration-300">
                  <Database size={32} />
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-4 tracking-tight">{t('trust.tech2Title')}</h3>
                <p className="text-sm text-slate-500 leading-relaxed font-medium">{t('trust.tech2Desc')}</p>
              </div>
              <div className="flex flex-col group">
                <div className="size-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-8 border border-emerald-100 group-hover:scale-110 transition-transform duration-300">
                  <Wallet size={32} />
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-4 tracking-tight">{t('trust.tech3Title')}</h3>
                <p className="text-sm text-slate-500 leading-relaxed font-medium">{t('trust.tech3Desc')}</p>
              </div>
            </div>
          </section>

          {/* Comparison Slider */}
          <section className="content-section bg-soft-bg">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-black tracking-tight mb-4 text-[#0F172A]">{t('trust.compareTitle')}</h2>
              <p className="text-slate-500 font-medium">{t('trust.compareSub')}</p>
            </div>
            <div
              ref={sliderContainerRef}
              onMouseMove={handleMouseMove}
              onTouchMove={handleMouseMove}
              className="max-w-6xl mx-auto relative rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl bg-white cursor-ew-resize"
            >
              <div className="flex h-[500px] relative overflow-hidden">
                <div className="w-full h-full absolute inset-0">
                  <img alt="Raw Output" className="w-full h-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDfBZh1OojvuN39gXV0reGvy23Do8afS2jLhjVgPdmzNqGqwlb5T6XeYrUxj04bmWyENiRLCWXE9g9C791Z6EQZGGWhrsBkS8oWZRVgm4ith1bnbISV_gito1wSXjdHtYL2IvGnX9lH0yyjgfEr6R8wsGxHDDdt8Y-LUxe3lsZdWphekgPH3dGLgjpI4Au2Gmag-39uJxmLRZw5RrusCUyOg0ggXX9_huLguCOIqk2A2asVX70eU4YLcsdyQNd10A8E8HRuLiHiOXEU" />
                  <div className="absolute top-8 left-8 bg-white/90 backdrop-blur px-4 py-2 rounded-lg border border-slate-200 shadow-sm">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic">{t('trust.rawOutput')}</span>
                  </div>
                </div>
                <div
                  className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm z-10 border-r-2 border-primary/40 overflow-hidden"
                  style={{ width: `${sliderPos}%`, left: '0' }}
                >
                  <div className="w-[1000px] h-full relative">
                    <div className="absolute inset-0 opacity-20 pointer-events-none" style={{backgroundImage: 'radial-gradient(#2563EB 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
                    <div className="p-12 text-blue-400 font-mono text-[10px] leading-relaxed uppercase">
                      <p className="mb-2 text-blue-100 font-bold">{t('trust.forensicData')}</p>
                      <p>{t('trust.status')}</p>
                      <p>0x45A2-FID: OCS-PRD-88219</p>
                      <p>TS: 2024-11-20T14:32:01.000Z</p>
                      <p>OWNER_HASH: e3b0c44298fc1c149afbf</p>
                      <p className="mt-4 text-emerald-400 flex items-center gap-2">
                        <ShieldIcon size={12} />
                        {t('trust.verifiedHash')}
                      </p>
                    </div>
                    <div className="absolute top-8 right-8 bg-primary text-white px-4 py-2 rounded-lg shadow-sm">
                        <span className="text-[10px] font-black uppercase tracking-widest">{t('trust.protectedLayer')}</span>
                    </div>
                  </div>
                </div>
                <div className="x-ray-divider" style={{ left: `${sliderPos}%` }}></div>
                <div className="x-ray-handle" style={{ left: `${sliderPos}%` }}>
                    <UnfoldHorizontalIcon size={16} className="text-slate-400" />
                </div>
              </div>
            </div>
          </section>

          {/* Legal Grid */}
          <section id="legal-framework" className="content-section">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-black tracking-tight mb-4 text-slate-900 uppercase tracking-widest">{t('trust.frameworkTitle')}</h2>
              <p className="text-slate-400 font-medium">{t('trust.frameworkSub')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                { icon: FileTextIcon, title: t('trust.licenseTitle'), desc: t('trust.licenseDesc') },
                { icon: GavelIcon, title: t('trust.dmcaPackageTitle'), desc: t('trust.dmcaPackageDesc') },
                { icon: VerifiedIcon, title: t('trust.complianceTitle'), desc: t('trust.complianceDesc') }
              ].map((item, i) => (
                <div key={i} className="p-8 border border-[#E2E8F0] bg-white rounded-2xl hover:shadow-lg transition-all duration-300 group">
                    <item.icon size={24} className="text-primary mb-4 group-hover:scale-110 transition-transform" />
                    <h4 className="font-bold text-slate-900 mb-2 uppercase text-xs tracking-widest">{item.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Automated Generator */}
          <section className="content-section bg-[#F8FAFC] border-t border-[#E2E8F0]">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-black tracking-tight mb-4 text-slate-900">{t('trust.generatorTitle')}</h2>
                <p className="text-slate-500 font-medium">{t('trust.generatorSub')}</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
                <div className="lg:col-span-2 space-y-8 bg-white p-8 rounded-2xl border border-[#E2E8F0] shadow-sm animate-in fade-in slide-in-from-left-4 duration-700">
                  <div className="space-y-4">
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400">{t('trust.urlLabel')}</label>
                    <input
                      value={infringingUrl}
                      onChange={(e) => setInfringingUrl(e.target.value)}
                      className="w-full bg-[#F8FAFC] border-[#E2E8F0] rounded-xl px-4 py-3 text-sm focus:ring-primary focus:border-primary outline-none transition-all"
                      placeholder="https://marketplace.com/stolen-listing-123"
                      type="text"
                    />
                  </div>
                  <div className="space-y-4">
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400">{t('trust.platformLabel')}</label>
                    <select
                      value={marketplace}
                      onChange={(e) => setMarketplace(e.target.value)}
                      className="w-full bg-[#F8FAFC] border-[#E2E8F0] rounded-xl px-4 py-3 text-sm focus:ring-primary focus:border-primary outline-none transition-all appearance-none cursor-pointer"
                    >
                      <option value="">{t('trust.selectPlatform')}</option>
                      <option value="Amazon Marketplace">Amazon Marketplace</option>
                      <option value="Ozon Global">Ozon Global</option>
                      <option value="eBay Security">eBay Security</option>
                      <option value="TikTok Shop Legal">TikTok Shop Legal</option>
                    </select>
                  </div>
                  <div className="space-y-4">
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400">{t('trust.evidenceLabel')}</label>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input checked readOnly className="rounded border-slate-300 text-primary focus:ring-primary h-5 w-5" type="checkbox"/>
                        <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">{t('trust.evidence1')}</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input checked readOnly className="rounded border-slate-300 text-primary focus:ring-primary h-5 w-5" type="checkbox"/>
                        <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">{t('trust.evidence2')}</span>
                      </label>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-[#E2E8F0] space-y-6">
                    <button
                      onClick={handleGenerate}
                      disabled={!isFormValid || generating || generated}
                      className={`w-full px-7 py-4 rounded-xl text-xs font-extrabold uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                          isFormValid && !generating && !generated
                          ? 'bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20 active:scale-95'
                          : generated
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                        {generating ? 'Processing...' : generated ? (
                            <>
                                <Check size={16} /> Downloaded
                            </>
                        ) : t('trust.downloadBtn')}
                    </button>
                    <div className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-blue-50 border border-blue-100">
                      <VerifiedIcon size={16} className="text-primary" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-primary">{t('trust.generatorNote')}</span>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-3 animate-in fade-in slide-in-from-right-4 duration-700 delay-150">
                  <div className="bg-white p-12 rounded-2xl border border-[#E2E8F0] shadow-xl min-h-[600px] relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-5">
                      <GavelIcon size={120} className="text-primary" />
                    </div>
                    <div className="font-serif text-slate-800 leading-relaxed text-sm space-y-6 relative z-10">
                      <div className="mb-12 border-b-2 border-slate-900 pb-4 flex justify-between items-end">
                        <h5 className="text-xl font-bold uppercase tracking-tighter">{t('trust.letterNotice')}</h5>
                        <p className="text-xs font-sans font-bold text-slate-400 uppercase tracking-widest">OCS-DISPUTE-2024-88</p>
                      </div>
                      <div className="grid grid-cols-2 gap-8 mb-8 text-[12px] font-sans uppercase font-bold text-slate-500">
                        <div>
                          <p className="mb-1">{t('trust.toLabel')}</p>
                          <p>Date: {new Date().toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="mb-1">{t('trust.refLabel')} {marketplace || '[PENDING]'}</p>
                          <p>{t('trust.priority')}</p>
                        </div>
                      </div>
                      <p className="mb-4">To Whom It May Concern,</p>
                      <p>{t('trust.letterBody1').replace('{url}', infringingUrl || '[PENDING_INPUT]')}</p>
                      <p>{t('trust.letterBody2')}</p>
                      <p>{t('trust.letterBody3')}</p>
                      <div className="bg-slate-50 p-4 rounded border border-slate-200 font-mono text-[11px] leading-tight text-slate-500 animate-in zoom-in duration-500">
                        # FORENSIC_VALIDATION_REPORT<br/>
                        VERIFIED_OWNER: OCS-CLIENT-USER-8291<br/>
                        NEURAL_SIGNATURE: FOUND [ID_82199]<br/>
                        PSD_TIMESTAMP: {new Date().toISOString()}<br/>
                        BLOCKCHAIN_ANCHOR: [VERIFIED_OCS_NODE]
                      </div>
                      <p>{t('trust.letterBody4')}</p>
                      <div className="mt-12 pt-8 border-t border-slate-100 italic opacity-60">
                        <p>{t('trust.letterSign')}</p>
                        <p className="font-bold mt-2">{t('trust.letterAutomation')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Additional Resources */}
          <section id="resources" className="content-section bg-[#F8FAFC] border-y border-[#E2E8F0]">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-primary border border-blue-100 text-[10px] font-bold uppercase tracking-widest mb-6">
                <ShieldCheckIcon size={14} />
                {t('feature3.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4 text-[#0F172A]">{t('feature3.title')}</h2>
              <p className="text-slate-500 font-medium max-w-2xl mx-auto">{t('feature3.subtitle')}</p>
            </div>
            <div className="flex flex-col items-center gap-10">
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 w-full">
                <div className="flex flex-col items-center md:items-start gap-3">
                  <button className="bg-white border border-[#E2E8F0] hover:border-slate-300 px-8 py-4 rounded-xl flex items-center gap-3 transition-all shadow-sm group active:scale-95">
                    <DownloadIcon size={18} className="text-slate-400 group-hover:text-primary transition-colors" />
                    <span className="text-xs font-black text-slate-900 uppercase tracking-widest">{t('feature3.downloadBtn')}</span>
                  </button>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('feature3.downloadSub')}</p>
                </div>
                <div className="h-px w-12 bg-slate-200 hidden md:block"></div>
                <button className="inline-flex items-center gap-2 text-xs font-black text-primary uppercase tracking-[0.2em] hover:gap-3 transition-all py-4">
                  {t('feature3.learnMore')}
                  <ArrowRightIcon size={14} />
                </button>
              </div>
            </div>
            <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div onClick={() => alert('Download started')} className="bg-white p-8 rounded-2xl border border-slate-100 flex items-center gap-6 hover:shadow-md transition-shadow cursor-pointer group active:scale-[0.98]">
                <div className="size-12 rounded-full bg-slate-50 flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors">
                  <FileTextIcon size={20} className="text-slate-400 group-hover:text-primary" />
                </div>
                <div>
                  <h5 className="text-xs font-black uppercase tracking-widest text-slate-900 mb-1">Copyright Whitepaper</h5>
                  <p className="text-xs text-slate-500">Full technical breakdown of our watermark technology.</p>
                </div>
                <button className="ml-auto size-10 flex items-center justify-center text-primary border border-slate-100 rounded-lg hover:bg-primary/5 transition-all"><DownloadIcon size={16} /></button>
              </div>
              <div onClick={() => alert('Download started')} className="bg-white p-8 rounded-2xl border border-slate-100 flex items-center gap-6 hover:shadow-md transition-shadow cursor-pointer group active:scale-[0.98]">
                <div className="size-12 rounded-full bg-slate-50 flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors">
                  <VerifiedIcon size={20} className="text-slate-400 group-hover:text-primary" />
                </div>
                <div>
                  <h5 className="text-xs font-black uppercase tracking-widest text-slate-900 mb-1">Legal Certificates</h5>
                  <p className="text-xs text-slate-500">Official templates for marketplace verification.</p>
                </div>
                <button className="ml-auto size-10 flex items-center justify-center text-primary border border-slate-100 rounded-lg hover:bg-primary/5 transition-all"><DownloadIcon size={16} /></button>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="px-6 md:px-12 pb-24 bg-white mt-24">
            <div className="bg-slate-900 rounded-[2.5rem] p-16 md:p-24 text-center text-white relative overflow-hidden group">
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-black mb-8 leading-tight tracking-tight group-hover:scale-105 duration-700">{t('trust.ctaTitle')} <br />{t('trust.ctaTitleBreak')}</h2>
                <p className="text-slate-400 text-lg mb-12 max-w-xl mx-auto font-medium">{t('trust.ctaSub')}</p>
                <button
                  onClick={() => openAuthModal('signup')}
                  className="bg-primary text-white px-10 py-5 rounded-full text-xs font-black uppercase tracking-[0.2em] hover:opacity-90 transition-all shadow-2xl shadow-primary/20 active:scale-95"
                >
                  {t('trust.ctaBtn')}
                </button>
              </div>
              <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-primary/10 transition-colors"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full -ml-32 -mb-32 blur-3xl group-hover:bg-white/10 transition-colors"></div>
            </div>
          </section>
        </main>

        <footer className="border-t border-slate-100 py-12 px-6 md:px-12 bg-white">
          <div className="flex flex-col md:flex-row justify-between items-center gap-10">
            <div className="flex items-center gap-2">
               <div className="size-6 bg-primary rounded flex items-center justify-center text-white">
                 <ShieldCheckIcon size={14} />
               </div>
               <span className="font-black text-sm tracking-tighter">{t('trustAppName')}</span>
            </div>
            <nav className="flex gap-10 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <a className="hover:text-primary transition-colors cursor-pointer">{t('footer.privacy')}</a>
              <a className="hover:text-primary transition-colors cursor-pointer" onClick={() => scrollToSection('legal-framework')}>{t('trust.nav.legal' as any)}</a>
              <a className="hover:text-primary transition-colors cursor-pointer">{t('footer.support')}</a>
            </nav>
            <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">{t('trust.rights')}</p>
          </div>
        </footer>
      </div>

      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="fixed bottom-8 left-8 flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-slate-200 px-4 py-2.5 rounded-full shadow-lg hover:shadow-xl hover:bg-white transition-all text-slate-600 text-xs font-bold uppercase tracking-widest group z-[100]"
      >
        <ArrowLeftIcon size={16} className="group-hover:-translate-x-1 transition-transform" />
        <span>Back Home</span>
      </button>
    </div>
  );
};
